/* eslint-disable react/prop-types */
import { useSnackbar } from 'notistack';
import { useEffect } from 'react';
import * as Yup from 'yup';
// form
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
// @mui
import { LoadingButton } from '@mui/lab';
import { Card, Stack, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';

import RHFAutoComplete from '../../../components/hook-form/RHFAutoComplete';
import useLocales from '../../../hooks/useLocales';
import { getTags, setTagSearch } from '../../../redux/slices/blog/blog.tag';
// components
import { FormProvider, RHFEditor } from '../../../components/hook-form';
import useAuth from '../../../hooks/useAuth';
import { setQuestionSearch } from '../../../redux/slices/blog/blog.question';
import { createQuestionAPI } from '../../../service/blog/blog.question.service';

// ----------------------------------------------------------------------

const RootStyles = styled(Card)(({ theme }) => ({
  padding: theme.spacing(1),
  borderRadius: Number(theme.shape.borderRadius),
}));

export default function QuestionNewForm() {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { translate } = useLocales();
  const theme = useTheme();
  const { isAuthenticated } = useAuth();

  const { search } = useSelector((state) => state.blogQuestion);
  const { tags, search: searchTag, isLoading: isLoadingTag } = useSelector((state) => state.blogTag);

  const NewItemSchema = Yup.object().shape({
    description: Yup.string().required(translate('validation.required'))
  });

  const defaultValues = {
    id: '',
    tags: [],
    description: '',
    status: "ACTIVE"
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      dispatch(getTags());
    }, 500);
    return () => clearTimeout(timeout);
  }, [searchTag]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFilterTitleTag = (_, value) => {
    dispatch(setTagSearch({ ...searchTag, value, page: 0, filterBys: { type: null } }));
  };

  const methods = useForm({
    resolver: yupResolver(NewItemSchema),
    defaultValues,
  });

  const { reset, handleSubmit, formState: { isSubmitting }, } = methods;

  const onSubmit = async (data) => {
    if (!isAuthenticated) {
      enqueueSnackbar(translate('label.signinToGPLX'), { variant: 'error' });
      return;
    }

    const resp = await createQuestionAPI(data);

    if (resp.code === '200') {
      reset();
      enqueueSnackbar(translate('message.createSuccess'));
      dispatch(setQuestionSearch({ ...search, page: 0, value: '', filterBys: { ...search.filterBys, tagIds: null } }));
    } else
      enqueueSnackbar(`${resp.code} - ${resp.message}`, { variant: 'error' });
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <RootStyles >
        <RHFEditor
          name="description"
          simple
          sx={{
            '& .ql-editor': {
              height: 90,
              minHeight: 80,
            },
            '& .ql-toolbar.ql-snow': {
              padding: '0px 8px',
            },
            '& .ql-formats': {
              marginRight: '0px!important',
            },
            '& .ql-container.ql-snow': {
              ...theme.typography.body1,
            },
          }}
          placeholder={translate('question.create')}
        />
        <Stack direction={'row'} spacing={2} justifyContent={'space-between'}>
          <RHFAutoComplete
            name="tags"
            multiple
            size="small"
            fullWidth={false}
            sx={{ minWidth: { md: 200, xs: 150 } }}
            label={translate('label.topic')}
            options={tags?.map(({ id, title }) => ({ id, title }))}
            onInputChange={handleFilterTitleTag}
            loading={isLoadingTag}
          />
          <LoadingButton size="small" type="submit" variant="contained" loading={isSubmitting} sx={{ minWidth: 100 }}>
            {translate('button.question')}
          </LoadingButton>
        </Stack>
      </RootStyles>
    </FormProvider>
  );
}
